import { useNavigate } from "react-router-dom"
import { Building2, Users, CheckCircle2, ArrowRight, Sparkles } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

interface GettingStartedCardProps {
  propertyCount: number
  tenantCount: number
}

export function GettingStartedCard({ propertyCount, tenantCount }: GettingStartedCardProps) {
  const navigate = useNavigate()

  // -1 = still loading — skip render to avoid flash
  if (propertyCount === -1 || tenantCount === -1) return null

  const propertyDone = propertyCount > 0
  const tenantDone = tenantCount > 0
  const completed = (propertyDone ? 1 : 0) + (tenantDone ? 1 : 0)

  // Both tasks complete — card disappears automatically
  if (completed === 2) return null

  const pct = (completed / 2) * 100

  return (
    <Card className="rounded-[2rem] border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-indigo-500/5 dark:from-primary/10 dark:via-card/60 dark:to-indigo-500/10 shadow-soft overflow-hidden mb-8">
      <CardContent className="p-5 md:p-7">
        {/* Header row */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold text-foreground text-base">Getting Started</h3>
              <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                {completed} / 2 done
              </span>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 w-full bg-slate-200/60 dark:bg-slate-700/40 rounded-full overflow-hidden mt-2">
              <div
                className="h-full bg-gradient-to-r from-primary to-indigo-400 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Task rows */}
        <div className="space-y-2.5">
          {/* Property task */}
          <div
            className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-all duration-300 ${
              propertyDone
                ? "bg-emerald-50/70 dark:bg-emerald-950/25 border-emerald-200/50 dark:border-emerald-800/30"
                : "bg-white/70 dark:bg-slate-800/40 border-slate-200/60 dark:border-slate-700/40"
            }`}
          >
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                propertyDone
                  ? "bg-emerald-100 dark:bg-emerald-500/20"
                  : "bg-blue-100 dark:bg-blue-500/10"
              }`}
            >
              {propertyDone ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold leading-tight ${
                  propertyDone ? "line-through text-muted-foreground" : "text-foreground"
                }`}
              >
                Add your first property
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {propertyDone
                  ? "Property added successfully"
                  : "Address, rooms, and photos"}
              </p>
            </div>

            {!propertyDone && (
              <Button
                size="sm"
                className="h-8 rounded-xl text-xs gap-1.5 flex-shrink-0 shadow-sm"
                onClick={() => navigate("/properties?new=1")}
              >
                Add <ArrowRight className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Tenant task */}
          <div
            className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-all duration-300 ${
              tenantDone
                ? "bg-emerald-50/70 dark:bg-emerald-950/25 border-emerald-200/50 dark:border-emerald-800/30"
                : "bg-white/70 dark:bg-slate-800/40 border-slate-200/60 dark:border-slate-700/40"
            }`}
          >
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                tenantDone
                  ? "bg-emerald-100 dark:bg-emerald-500/20"
                  : "bg-indigo-100 dark:bg-indigo-500/10"
              }`}
            >
              {tenantDone ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Users className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold leading-tight ${
                  tenantDone ? "line-through text-muted-foreground" : "text-foreground"
                }`}
              >
                Add your first tenant
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {tenantDone
                  ? "Tenant added successfully"
                  : "Profile, contact details & lease"}
              </p>
            </div>

            {!tenantDone && (
              <Button
                size="sm"
                className="h-8 rounded-xl text-xs gap-1.5 flex-shrink-0 shadow-sm"
                onClick={() => navigate("/tenants?new=1")}
              >
                Add <ArrowRight className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
